import { useState, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { CalendarIcon, Plus, X, Users, ShoppingBag, IndianRupee, AlertCircle, AlertTriangle, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { validateEmail } from "@/lib/emailValidation";
import { InviteLinkSettings } from "@/components/InviteLinkSettings";

interface AddGroupPurchaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizerEmail: string;
  organizerName?: string;
}

interface InvitedFriend {
  email: string;
  name: string;
}

// Wizard steps definition
const STEPS = [
  { id: 1, name: "Basics", description: "What & when" },
  { id: 2, name: "Budget", description: "Price range" },
  { id: 3, name: "Invite", description: "Add friends" },
  { id: 4, name: "Review", description: "Confirm" },
];

// Animation variants for step transitions
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

export function AddGroupPurchaseDialog({
  open,
  onOpenChange,
  organizerEmail,
  organizerName,
}: AddGroupPurchaseDialogProps) {
  const navigate = useNavigate();
  
  // Wizard step state
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0);
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetDate, setTargetDate] = useState<Date | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [invitedFriends, setInvitedFriends] = useState<InvitedFriend[]>([]);
  const [newFriendEmail, setNewFriendEmail] = useState("");
  const [newFriendName, setNewFriendName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  // Email validation state
  const [emailError, setEmailError] = useState<string>("");
  
  // Unsaved changes warning
  const [showExitWarning, setShowExitWarning] = useState(false);
  
  // Invite link settings state
  const [inviteLinkEnabled, setInviteLinkEnabled] = useState(false);
  const [inviteLinkApprovalMode, setInviteLinkApprovalMode] = useState<"auto" | "manual">("auto");
  const [maxSelfInvites, setMaxSelfInvites] = useState<number | null>(null);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return title.trim() !== "" || 
           description.trim() !== "" || 
           invitedFriends.length > 0 ||
           budgetMin !== "" ||
           budgetMax !== "";
  }, [title, description, invitedFriends.length, budgetMin, budgetMax]);

  // Real-time email validation
  const validateEmailInput = useCallback((email: string): string => {
    if (!email.trim()) return "";
    const result = validateEmail(email.trim());
    return result.valid ? "" : (result.error || "Invalid email");
  }, []);

  const handleEmailChange = (value: string) => {
    setNewFriendEmail(value);
    if (value.length > 3) {
      setEmailError(validateEmailInput(value));
    } else {
      setEmailError("");
    }
  };

  // Field validation helper
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'title':
        return value.trim() ? '' : 'Title is required';
      default:
        return '';
    }
  };

  const handleFieldChange = (field: string, value: string, setter: (val: string) => void) => {
    setter(value);
    if (touched[field]) {
      const error = validateField(field, value);
      setFieldErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleFieldBlur = (field: string, value: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  // Inline error component
  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1.5 text-destructive mt-1.5">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-xs">{error}</span>
      </div>
    );
  };

  // Step validation
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!title.trim()) {
          setTouched(prev => ({ ...prev, title: true }));
          setFieldErrors(prev => ({ ...prev, title: 'Title is required' }));
          return false;
        }
        if (!targetDate) {
          setTouched(prev => ({ ...prev, targetDate: true }));
          setFieldErrors(prev => ({ ...prev, targetDate: 'Target date is required' }));
          toast.error("Please select a target date");
          return false;
        }
        return true;
      case 2:
        return true; // Budget is optional
      case 3:
        return true; // Invitations are optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setDirection(1);
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const resetForm = () => {
    setCurrentStep(1);
    setTouched({});
    setFieldErrors({});
    setTitle("");
    setDescription("");
    setTargetDate(undefined);
    setBudgetMin("");
    setBudgetMax("");
    setInvitedFriends([]);
    setNewFriendEmail("");
    setNewFriendName("");
    setEmailError("");
    setInviteLinkEnabled(false);
    setInviteLinkApprovalMode("auto");
    setMaxSelfInvites(null);
  };

  const attemptDialogClose = () => {
    if (hasUnsavedChanges) {
      setShowExitWarning(true);
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleAddFriend = () => {
    if (!newFriendEmail.trim()) {
      setEmailError("Email is required");
      return;
    }
    
    const emailValidation = validateEmail(newFriendEmail.trim());
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error || "Invalid email address");
      return;
    }

    if (invitedFriends.some(f => f.email.toLowerCase() === newFriendEmail.toLowerCase().trim())) {
      setEmailError("This email is already added");
      return;
    }

    if (newFriendEmail.toLowerCase().trim() === organizerEmail.toLowerCase()) {
      setEmailError("You can't invite yourself");
      return;
    }

    setInvitedFriends([...invitedFriends, { 
      email: newFriendEmail.trim(), 
      name: newFriendName.trim() || newFriendEmail.split('@')[0] 
    }]);
    setNewFriendEmail("");
    setNewFriendName("");
    setEmailError("");
  };

  const handleRemoveFriend = (email: string) => {
    setInvitedFriends(invitedFriends.filter(f => f.email !== email));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { error: insertError } = await supabase
        .from("group_purchases")
        .insert({
          organizer_email: organizerEmail,
          organizer_name: organizerName || organizerEmail.split('@')[0],
          title: title.trim(),
          description: description.trim() || null,
          target_date: targetDate ? format(targetDate, 'yyyy-MM-dd') : null,
          target_budget_min: budgetMin ? parseFloat(budgetMin) : null,
          target_budget_max: budgetMax ? parseFloat(budgetMax) : null,
          invite_link_enabled: inviteLinkEnabled,
          invite_link_approval_mode: inviteLinkApprovalMode,
          max_self_invites: maxSelfInvites,
          category: 'purchase',
        });

      if (insertError) throw insertError;

      const { data: purchases, error: fetchError } = await supabase
        .from("group_purchases")
        .select("*")
        .eq("organizer_email", organizerEmail)
        .eq("title", title.trim())
        .order("created_at", { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;
      if (!purchases || purchases.length === 0) throw new Error("Failed to find created purchase");
      
      const purchase = purchases[0];

      if (invitedFriends.length > 0) {
        const participantsToInsert = invitedFriends.map(friend => ({
          purchase_id: purchase.id,
          email: friend.email,
          name: friend.name,
          invited_by: organizerEmail,
        }));

        const { error: participantsError } = await supabase
          .from("group_purchase_participants")
          .insert(participantsToInsert);

        if (!participantsError) {
          try {
            await supabase.functions.invoke("send-group-purchase-invitation", {
              body: {
                purchaseId: purchase.id,
                purchaseTitle: title.trim(),
                organizerName: organizerName || organizerEmail.split('@')[0],
                organizerEmail: organizerEmail,
                participants: invitedFriends,
              },
            });
          } catch (emailError) {
            console.error("Error sending invitation emails:", emailError);
          }
        }
      }

      toast.success("Group purchase created! Redirecting to discussion...");
      resetForm();
      onOpenChange(false);
      navigate(`/purchases/${purchase.id}`);
    } catch (error: any) {
      console.error("Error creating group purchase:", error);
      toast.error("Failed to create group purchase");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={attemptDialogClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-purchase" />
            Start a Group Purchase
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                      currentStep > step.id
                        ? "bg-purchase text-purchase-foreground"
                        : currentStep === step.id
                        ? "bg-purchase text-purchase-foreground"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className={cn(
                    "text-xs mt-1 hidden sm:block",
                    currentStep >= step.id ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      "w-8 sm:w-12 h-0.5 mx-1",
                      currentStep > step.id ? "bg-blue-600" : "bg-muted"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content with Animation */}
        <div className="relative overflow-hidden min-h-[280px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              className="space-y-5 py-2"
            >
              {/* Step 1: Basics */}
              {currentStep === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="title" className="flex items-center gap-1">
                      What are you buying together?
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="title"
                      placeholder="e.g., Birthday gift for Mom, Office coffee machine..."
                      value={title}
                      onChange={(e) => handleFieldChange('title', e.target.value, setTitle)}
                      onBlur={() => handleFieldBlur('title', title)}
                      className={cn(touched.title && fieldErrors.title && "border-destructive")}
                    />
                    <FieldError error={touched.title ? fieldErrors.title : undefined} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description (optional)</Label>
                    <Textarea
                      id="description"
                      placeholder="Add any details about the purchase..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Target Date (optional)</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !targetDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {targetDate ? format(targetDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={targetDate}
                          onSelect={(d) => {
                            setTargetDate(d);
                            setCalendarOpen(false);
                          }}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}

              {/* Step 2: Budget */}
              {currentStep === 2 && (
                <>
                  <div className="space-y-2">
                    <Label>Budget Range (optional)</Label>
                    <p className="text-xs text-muted-foreground">Help your group decide on a price range</p>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          placeholder="Min"
                          value={budgetMin}
                          onChange={(e) => setBudgetMin(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <span className="text-muted-foreground">to</span>
                      <div className="relative flex-1">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={budgetMax}
                          onChange={(e) => setBudgetMax(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-purchase/10 border border-purchase/20">
                    <p className="text-sm text-purchase dark:text-purchase">
                      💡 Members can vote on their preferred budget in the discussion page
                    </p>
                  </div>
                </>
              )}

              {/* Step 3: Invite */}
              {currentStep === 3 && (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-purchase" />
                      <Label className="text-sm font-medium">Invite Friends</Label>
                    </div>

                    {invitedFriends.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-md border">
                        {invitedFriends.map((friend) => (
                          <div key={friend.email} className="flex items-center gap-1 bg-purchase/15 text-purchase px-2.5 py-1 rounded-full text-xs font-medium">
                            <span>{friend.name || friend.email}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFriend(friend.email)}
                              className="ml-1 hover:text-destructive transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Friend's email"
                          value={newFriendEmail}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          className={cn("flex-1", emailError && "border-destructive")}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddFriend}
                          disabled={!newFriendEmail.trim()}
                          className="h-10 px-3 gap-1.5"
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                      <FieldError error={emailError} />
                      <Input
                        placeholder="Friend's name (optional)"
                        value={newFriendName}
                        onChange={(e) => setNewFriendName(e.target.value)}
                      />
                    </div>

                    <InviteLinkSettings
                      enabled={inviteLinkEnabled}
                      onEnabledChange={setInviteLinkEnabled}
                      approvalMode={inviteLinkApprovalMode}
                      onApprovalModeChange={setInviteLinkApprovalMode}
                      maxSelfInvites={maxSelfInvites}
                      onMaxSelfInvitesChange={setMaxSelfInvites}
                    />
                  </div>
                </>
              )}

              {/* Step 4: Review */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <h3 className="font-semibold text-lg mb-3">{title}</h3>
                    {description && (
                      <p className="text-sm text-muted-foreground mb-3">{description}</p>
                    )}
                    <div className="space-y-2 text-sm">
                      {targetDate && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Target Date</span>
                          <span>{format(targetDate, "PPP")}</span>
                        </div>
                      )}
                      {(budgetMin || budgetMax) && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Budget Range</span>
                          <span>₹{budgetMin || '0'} - ₹{budgetMax || '∞'}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Participants</span>
                        <span>{invitedFriends.length + 1} (incl. you)</span>
                      </div>
                      {inviteLinkEnabled && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invite Link</span>
                          <Badge variant="outline" className="text-xs">Enabled</Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    You can configure payment collection in the discussion page
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? attemptDialogClose : handleBack}
            disabled={isSubmitting}
          >
            {currentStep === 1 ? "Cancel" : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </>
            )}
          </Button>

          {currentStep < STEPS.length ? (
            <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? "Creating..." : "Create Group Purchase"}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showExitWarning} onOpenChange={setShowExitWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-center">Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              You have unsaved changes. Are you sure you want to close?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowExitWarning(false);
                onOpenChange(false);
                resetForm();
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
